import type { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import OrderLineItem from './OrderLineItem'
import type { HardwareSupplierFilterKeyType, ProjectOrderDetail, SystemDesignType } from './type'

export const MANUAL_SOURCE_TYPES = ['exclude', '3rd_party', 'stock_on_hand', 'component'] as const

export type ManualSourceType = typeof MANUAL_SOURCE_TYPES[number]

export const SELECTABLE_MANUAL_SOURCES = ['exclude', '3rd_party', 'stock_on_hand'] as const

export const NO_PRICE_SOURCES = [...MANUAL_SOURCE_TYPES, 'unknown', 'manual'] as const

export const toDisplaySourceValue = (value: string | undefined): string => {
  if (!value || value === 'unknown') return 'default'
  return value === 'component' ? 'exclude' : value
}

export const fromDisplaySourceValue = (displayValue: string): string | undefined =>
  displayValue === 'default' ? undefined : displayValue

export const isManualSource = (s: string | undefined): boolean =>
  !!s && (MANUAL_SOURCE_TYPES as readonly string[]).includes(s)

export type ResolvedSourceType = HardwareSupplierFilterKeyType | undefined

export function getResolvedSource(
  selectedDistributor: string | undefined,
  orderingData: DistributorDataTypeV2[] | undefined,
  options?: { hasMixedSources?: boolean; isSingleProject?: boolean; isManualCost?: boolean }
): ResolvedSourceType {
  const availableDistributors = orderingData
    ? orderingData
        .filter((d) => d.is_available)
        .map((d) => d.distributor)
        .filter((d): d is HardwareSupplierFilterKeyType => !!d)
    : []

  if (options?.hasMixedSources) return undefined
  if (options?.isSingleProject && options?.isManualCost) {
    const src = selectedDistributor
    if (src && (SELECTABLE_MANUAL_SOURCES as readonly string[]).includes(src)) return src
    return 'exclude'
  }

  if (selectedDistributor && isManualSource(selectedDistributor)) return selectedDistributor
  if (selectedDistributor && availableDistributors.includes(selectedDistributor as HardwareSupplierFilterKeyType)) {
    return selectedDistributor as HardwareSupplierFilterKeyType
  }
  return availableDistributors[0]
}

/** Unique key for a project allocation (projectId + systemId). Used for grouping and lookups. */
export const getProjectOrderAllocationKey = (po: Pick<ProjectOrderDetail, 'projectId' | 'systemId'>): string =>
  `${po.projectId}-${po.systemId ?? 'default'}`

export function getOrderCost(orderableLineItems: OrderLineItem[], applyDiscounts: boolean = true): number {
  return orderableLineItems.reduce((sum, lineItem) => {
    const { beforeDiscount, afterDiscount } = lineItem.getBestDiscountOffer()
    if (lineItem.bulkOption) {
      return sum + lineItem.bulkPrice!
    }
    if (applyDiscounts) {
      if (!afterDiscount) {
        return sum
      }
      return sum + afterDiscount
    } else {
      if (!beforeDiscount) {
        return sum
      }
      return sum + beforeDiscount
    }
  }, 0)
}

export const sortByPriceOrderingV2 = <T extends { [key: string]: any }>(arr: T[], ascending: boolean = true): T[] => {
  return [...arr].sort((a, b) => {
    return ascending ? a.price - b.price : b.price - a.price
  })
}

export const getOutputPower = (rating: number) => {
  return rating * 1000
}

/** Distributor catalog name for a line item, with custom "other" component title as fallback. */
export function getLineItemProductDisplayName(
  distributorData: { short_description?: string } | undefined,
  componentTitle?: string
): string {
  return distributorData?.short_description || componentTitle || ''
}

/**
 * Compare cart items against BOM items for a given project/system.
 * Returns true when every code+quantity in the BOM has an exact match in the cart allocations, and vice-versa.
 * When a line item has a single projectOrder entry for this project/system, the comparison uses the line
 * item's actual quantity so cart edits on the cart page are reflected (e.g. "Update Cart" when qty differs).
 */
export function cartMatchesProjectBom(
  cartLineItems: Pick<OrderLineItem, 'code' | 'projectOrder' | 'quantity'>[],
  bomItems: { code: string; quantity: number }[],
  projectId: number,
  systemId?: number
): boolean {
  const cartMap = new Map<string, number>()
  for (const li of cartLineItems) {
    const matching = li.projectOrder?.filter(
      (po) => po.projectId === projectId && po.systemId === systemId
    ) ?? []
    const quantityForThisProject =
      matching.length === 1 && (li.projectOrder?.length ?? 0) === 1
        ? li.quantity
        : matching.reduce((sum, po) => sum + po.requiredQuantity, 0)
    if (matching.length > 0) {
      cartMap.set(li.code, (cartMap.get(li.code) ?? 0) + quantityForThisProject)
    }
  }

  const bomMap = new Map<string, number>()
  for (const item of bomItems) {
    bomMap.set(item.code, (bomMap.get(item.code) ?? 0) + item.quantity)
  }

  if (cartMap.size !== bomMap.size) return false
  for (const [code, qty] of bomMap) {
    if (cartMap.get(code) !== qty) return false
  }
  return true
}

/**
 * Extract {code, quantity} pairs from a SystemDesignType for BOM comparison.
 * Uses the same merge logic as getLineItemsFromSystemDesignDataWithComponents: bom_line_items
 * first, then category arrays, skipping codes already present so the "expected" BOM matches
 * what is added to the cart (and the eye preview). Ensures "Update Cart" shows when cart
 * quantity differs from design (e.g. 11 in cart vs 12 in BOM).
 */
export function extractBomItemsFromSystemDesign(
  systemDesign: SystemDesignType
): { code: string; quantity: number }[] {
  const items: { code: string; quantity: number }[] = []
  const processedCodes = new Set<string>()

  if (systemDesign.bom_line_items) {
    for (const item of systemDesign.bom_line_items) {
      if (item.code) {
        items.push({ code: item.code, quantity: item.quantity })
        processedCodes.add(item.code)
      }
    }
  }

  const categories: Array<'batteries' | 'inverters' | 'modules' | 'others'> = [
    'batteries',
    'inverters',
    'modules',
    'others',
  ]
  for (const category of categories) {
    const catItems = systemDesign[category]
    if (catItems) {
      for (const item of catItems) {
        if (item.code && !processedCodes.has(item.code)) {
          items.push({ code: item.code, quantity: item.quantity })
          processedCodes.add(item.code)
        }
      }
    }
  }

  return items
}
